/**
 * Agent Reporting - Per-agent list with activity metrics + by-market tab
 *
 * Route: /admin/reporting/agents
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { format, formatDistanceToNow, subDays, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears } from 'date-fns'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Button } from '@/common/components/button'
import { DatePicker } from '@/common/components/date-picker'
import { Multiselect } from '@/common/components/multiselect'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Icon } from '@/common/components/icon'
import { Tooltip as InfoTooltip } from '@/common/components/tooltip'
import { useAuth } from '@/common/hooks/useAuth'
import { useMarkets } from '@/common/hooks/useMarkets'
import { useAgentList, useAgentsByMarket } from '@/features/reporting/hooks/useReportingData'
import { fetchAgentList, fetchAgentMarketRecords, type AgentListItem, type AgentListParams, type AgentsByMarketItem, type MarketActivityRecordItem } from '@/features/reporting/api/reportingApi'
import { mapAgentsByMarketPage, fetchAllAgentsByMarket, agentsByMarketFilename } from '@/features/reporting/utils/agentExportUtils'
import { ExportMenu } from '@/features/reporting/components/ExportMenu'
import { table } from '@/features/reporting/chartPalette'
import * as styles from '../index.css'

// =============================================================================
// Constants & Helpers
// =============================================================================

type SortDir = 'asc' | 'desc'
type TabValue = 'individual' | 'by-market'
type TimeFramePreset = 'all' | 'last_7' | 'last_30' | 'last_month' | 'this_month' | 'last_year' | 'this_year' | 'custom'
type DrillDownType = 'agents' | 'submissions' | 'offers' | 'uc' | 'closed'
type AgentTypeFilter = 'all' | 'internal' | 'external'

const AGENT_TYPE_OPTIONS: { value: AgentTypeFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'internal', label: 'Internal (Orchard)' },
  { value: 'external', label: 'External (3P)' },
]

/** Translate the UI's single-value picker to the API's list-shaped param. */
function resolveAgentType(value: AgentTypeFilter): string[] | undefined {
  return value === 'all' ? undefined : [value]
}

const TIME_FRAME_OPTIONS: { value: TimeFramePreset; label: string }[] = [
  { value: 'all', label: 'All Time' },
  { value: 'last_7', label: 'Last 7 Days' },
  { value: 'last_30', label: 'Last 30 Days' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom' },
]

function resolveTimeFrame(
  preset: TimeFramePreset,
  customStart?: Date | null,
  customEnd?: Date | null,
): { start?: string; end?: string } {
  const today = new Date()
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd')
  switch (preset) {
    case 'all':
      return {}
    case 'last_7':
      return { start: fmt(subDays(today, 7)) }
    case 'last_30':
      return { start: fmt(subDays(today, 30)) }
    case 'last_month': {
      const prev = subMonths(today, 1)
      return { start: fmt(startOfMonth(prev)), end: fmt(endOfMonth(prev)) }
    }
    case 'this_month':
      return { start: fmt(startOfMonth(today)) }
    case 'last_year': {
      const prev = subYears(today, 1)
      return { start: fmt(startOfYear(prev)), end: fmt(endOfYear(prev)) }
    }
    case 'this_year':
      return { start: fmt(startOfYear(today)) }
    case 'custom':
      return {
        start: customStart ? fmt(customStart) : undefined,
        end: customEnd ? fmt(customEnd) : undefined,
      }
  }
}

function formatLastLogin(isoStr: string | null): string {
  if (!isoStr) return 'Never'
  return formatDistanceToNow(new Date(isoStr), { addSuffix: true })
}

function formatRate(value: number | null): string {
  if (value == null) return 'N/A'
  return `${value.toFixed(1)}%`
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return format(new Date(dateStr), 'MMM d, yyyy')
}

// =============================================================================
// Column tooltips
// =============================================================================

const COLUMN_TOOLTIPS: Record<string, string> = {
  name: 'Agent\'s full name from their COM platform profile (Source: Platform)',
  email: 'Registered email address (Source: Platform)',
  agent_type: 'Internal = Orchard agent; External = third-party partner agent (Source: Platform)',
  properties_submitted: 'Total properties submitted by this agent in the selected date range (Source: Platform)',
  properties_with_offers: 'Properties that received at least one investor offer (Source: Platform Activity)',
  submission_to_offer_rate: 'Properties with offers / Submitted properties as a percentage (Source: Platform Activity)',
  gone_uc: 'This agent\'s properties currently under contract (Source: Platform)',
  closed: 'This agent\'s properties that have sold (Source: Platform)',
  uc_to_close_rate: 'Closed / (UC + Closed) as a percentage (Source: Platform)',
  last_login: 'Last time this agent logged into the platform (Source: Platform)',
}

const BY_MARKET_TOOLTIPS: Record<string, string> = {
  market: 'Market name (Source: Platform)',
  agents: 'Agents who submitted at least one property in this market (Source: Platform)',
  submitted: 'Total properties submitted by agents in this market (Source: Platform)',
  offers: 'Properties that received at least one investor offer in this market (Source: Platform Activity)',
  sub_to_offer: 'Properties with offers / Submitted as a percentage (Source: Platform Activity)',
  uc: 'Properties under contract in this market (Source: Platform)',
  closed: 'Properties sold in this market (Source: Platform)',
  uc_to_close: 'Closed / (UC + Closed) as a percentage (Source: Platform)',
}

// =============================================================================
// Column definitions
// =============================================================================

interface ColumnDef {
  key: string
  label: string
  sortKey?: string
  align?: 'left' | 'right'
  optional?: boolean
  render: (agent: AgentListItem) => string | React.ReactNode
}

const DEFAULT_VISIBLE = [
  'name', 'email', 'properties_submitted', 'properties_with_offers',
  'submission_to_offer_rate', 'gone_uc', 'closed', 'uc_to_close_rate', 'last_login',
]

const LOCAL_STORAGE_KEY = 'agent-report-columns'

function getStoredColumns(): string[] {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as string[]
      if (Array.isArray(parsed) && parsed.length > 0) return parsed
    }
  } catch { /* ignore */ }
  return DEFAULT_VISIBLE
}

// =============================================================================
// Info icon + column header
// =============================================================================

const InfoIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zm.75 7.75v-4h-1.5v4h1.5zM8 5.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z" />
  </svg>
)

function ColumnHeader({ label, tooltip, sortIndicator, align = 'left' }: {
  label: string
  tooltip: string
  sortIndicator?: string
  align?: 'left' | 'right'
}) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start', gap: '3px' }}>
      {label}{sortIndicator}
      <InfoTooltip content={tooltip} side="top" delayDuration={200}>
        <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', color: '#9ca3af', cursor: 'help', flexShrink: 0 }}>
          <InfoIcon />
        </span>
      </InfoTooltip>
    </div>
  )
}

// =============================================================================
// Route
// =============================================================================

export const Route = createFileRoute({
  component: AgentsPage,
  errorComponent: ({ error }) => (
    <div style={{ padding: '24px' }}>
      <h2>Error loading Agents page</h2>
      <pre style={{ color: 'red', whiteSpace: 'pre-wrap' }}>{error?.message ?? String(error)}</pre>
    </div>
  ),
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (['individual', 'by-market'].includes(search.tab as string) ? (search.tab as TabValue) : 'individual'),
    sortBy: (search.sortBy as string) || 'last_login',
    sortDir: (['asc', 'desc'].includes(search.sortDir as string) ? (search.sortDir as SortDir) : 'desc'),
  }),
})

// =============================================================================
// Main Component
// =============================================================================

function AgentsPage() {
  const { token } = useAuth()
  const navigate = useNavigate()
  const searchParams = Route.useSearch()
  const { tab, sortBy, sortDir } = searchParams

  const updateSearch = useCallback(
    (updates: Partial<typeof searchParams>) => {
      void navigate({ to: '.', search: { ...searchParams, ...updates }, replace: true })
    },
    [navigate, searchParams]
  )

  // --- Filter state ---
  const [timeFrame, setTimeFrame] = useState<TimeFramePreset>('all')
  const [customStart, setCustomStart] = useState<Date | null>(null)
  const [customEnd, setCustomEnd] = useState<Date | null>(null)
  const [loginTimeFrame, setLoginTimeFrame] = useState<TimeFramePreset>('all')
  const [loginCustomStart, setLoginCustomStart] = useState<Date | null>(null)
  const [loginCustomEnd, setLoginCustomEnd] = useState<Date | null>(null)
  const [filterMarkets, setFilterMarkets] = useState<string[]>([])
  const [agentTypeFilter, setAgentTypeFilter] = useState<AgentTypeFilter>('all')
  const [searchText, setSearchText] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Applied filters (only update on Apply click)
  const [appliedFilters, setAppliedFilters] = useState<{
    start_date?: string
    end_date?: string
    market?: string[]
    agent_type?: string[]
    search?: string
    login_start?: string
    login_end?: string
  }>({})

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<string[]>(getStoredColumns)
  const [showColumnPicker, setShowColumnPicker] = useState(false)

  // Drill-down state (by-market tab)
  const [selectedMarket, setSelectedMarket] = useState<string | null>(null)
  const [drillDownType, setDrillDownType] = useState<DrillDownType>('agents')
  const [drillDownPage, setDrillDownPage] = useState(1)

  // Markets data
  const { groupedMarketOptions, loading: marketsLoading } = useMarkets()

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchText])

  // Reset page when filters/sort change
  useEffect(() => {
    setPage(1)
  }, [appliedFilters, sortBy, sortDir])

  // Reset drill-down when filters change
  useEffect(() => {
    setSelectedMarket(null)
    setDrillDownPage(1)
  }, [appliedFilters])

  // Apply filters handler
  const handleApplyFilters = useCallback(() => {
    const tf = resolveTimeFrame(timeFrame, customStart, customEnd)
    const login = resolveTimeFrame(loginTimeFrame, loginCustomStart, loginCustomEnd)
    setAppliedFilters({
      start_date: tf.start,
      end_date: tf.end,
      market: filterMarkets.length > 0 ? filterMarkets : undefined,
      agent_type: resolveAgentType(agentTypeFilter),
      search: debouncedSearch || undefined,
      login_start: login.start,
      login_end: login.end,
    })
    setPage(1)
  }, [timeFrame, customStart, customEnd, loginTimeFrame, loginCustomStart, loginCustomEnd, filterMarkets, agentTypeFilter, debouncedSearch])

  // Clear filters handler
  const handleClearFilters = useCallback(() => {
    setTimeFrame('all')
    setCustomStart(null)
    setCustomEnd(null)
    setLoginTimeFrame('all')
    setLoginCustomStart(null)
    setLoginCustomEnd(null)
    setFilterMarkets([])
    setAgentTypeFilter('all')
    setSearchText('')
    setDebouncedSearch('')
    setAppliedFilters({})
    setPage(1)
  }, [])

  // Auto-apply search on debounce
  useEffect(() => {
    setAppliedFilters((prev) => {
      const next = debouncedSearch || undefined
      if (prev.search === next) return prev
      return { ...prev, search: next }
    })
  }, [debouncedSearch])

  // --- API params ---
  const apiParams: AgentListParams = useMemo(() => ({
    ...appliedFilters,
    sort_by: sortBy,
    sort_dir: sortDir,
    page,
    page_size: pageSize,
  }), [appliedFilters, sortBy, sortDir, page, pageSize])

  const { data: agentsRes, isFetching, isLoading: agentsLoading } = useAgentList(apiParams)
  const byMarketFilters = useMemo(() => ({
    start_date: appliedFilters.start_date,
    end_date: appliedFilters.end_date,
    market: appliedFilters.market,
    agent_type: appliedFilters.agent_type,
    search: appliedFilters.search,
    login_start: appliedFilters.login_start,
    login_end: appliedFilters.login_end,
  }), [appliedFilters])
  const { data: byMarketRes, isFetching: byMarketFetching } = useAgentsByMarket(byMarketFilters)

  const agents = useMemo(() => agentsRes?.data ?? [], [agentsRes?.data])
  const pagination = agentsRes?.pagination
  const byMarketData = useMemo(() => byMarketRes?.data ?? [], [byMarketRes?.data])

  // --- Drill-down queries ---
  const isAgentDrill = drillDownType === 'agents'

  // Agent drill-down: reuses fetchAgentList filtered by market (like investor page)
  const agentDrillParams = useMemo(() => {
    if (!selectedMarket || !isAgentDrill) return null
    return {
      ...appliedFilters,
      market: [selectedMarket],
      sort_by: sortBy,
      sort_dir: sortDir,
      page: drillDownPage,
      page_size: 25,
    }
  }, [selectedMarket, isAgentDrill, appliedFilters, sortBy, sortDir, drillDownPage])

  const { data: agentDrillRes, isFetching: agentDrillFetching } = useQuery({
    queryKey: ['reporting', 'agents', 'drill-down', agentDrillParams],
    queryFn: () => fetchAgentList(token!, agentDrillParams!),
    enabled: !!token && !!agentDrillParams,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  })

  // Activity drill-down: uses fetchAgentMarketRecords (submissions/offers/uc/closed)
  const activityDrillParams = useMemo(() => {
    if (!selectedMarket || isAgentDrill) return null
    return {
      market: selectedMarket,
      record_type: drillDownType,
      start_date: appliedFilters.start_date,
      end_date: appliedFilters.end_date,
      agent_type: appliedFilters.agent_type,
      page: drillDownPage,
      page_size: 25,
    }
  }, [selectedMarket, isAgentDrill, drillDownType, appliedFilters, drillDownPage])

  const { data: activityDrillRes, isFetching: activityDrillFetching } = useQuery({
    queryKey: ['reporting', 'agents', 'by-market', 'activity-drill', activityDrillParams],
    queryFn: () => fetchAgentMarketRecords(token!, activityDrillParams!),
    enabled: !!token && !!activityDrillParams,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  })

  const drillDownFetching = agentDrillFetching || activityDrillFetching
  const drillDownPagination = isAgentDrill ? agentDrillRes?.pagination : activityDrillRes?.pagination

  // --- Sort ---
  const handleSort = useCallback((column: string) => {
    if (sortBy === column) {
      updateSearch({ sortDir: sortDir === 'asc' ? 'desc' : 'asc' })
    } else {
      updateSearch({ sortBy: column, sortDir: 'desc' })
    }
  }, [sortBy, sortDir, updateSearch])

  const sortIndicator = (column: string) => {
    if (sortBy !== column) return ''
    return sortDir === 'asc' ? ' \u25B2' : ' \u25BC'
  }

  // --- Column config ---
  const allColumns: ColumnDef[] = useMemo(() => [
    { key: 'name', label: 'Agent Name', sortKey: 'name', render: (a) => `${a.first_name} ${a.last_name}` },
    { key: 'email', label: 'Email', sortKey: 'email', optional: true, render: (a) => a.email ?? '-' },
    {
      key: 'agent_type',
      label: 'Type',
      sortKey: 'agent_type',
      optional: true,
      render: (a) => (a.agent_type ? a.agent_type.charAt(0).toUpperCase() + a.agent_type.slice(1) : '-'),
    },
    { key: 'properties_submitted', label: 'Submitted', sortKey: 'properties_submitted', align: 'right', render: (a) => a.properties_submitted.toLocaleString() },
    { key: 'properties_with_offers', label: 'w/ Offers', sortKey: 'properties_with_offers', align: 'right', render: (a) => a.properties_with_offers.toLocaleString() },
    { key: 'submission_to_offer_rate', label: 'Sub to Offer', sortKey: 'submission_to_offer_rate', align: 'right', optional: true, render: (a) => formatRate(a.submission_to_offer_rate) },
    { key: 'gone_uc', label: 'Gone UC', sortKey: 'gone_uc', align: 'right', optional: true, render: (a) => a.gone_uc.toLocaleString() },
    { key: 'closed', label: 'Closed', sortKey: 'closed', align: 'right', optional: true, render: (a) => a.closed.toLocaleString() },
    { key: 'uc_to_close_rate', label: 'UC to Close', sortKey: 'uc_to_close_rate', align: 'right', optional: true, render: (a) => formatRate(a.uc_to_close_rate) },
    { key: 'last_login', label: 'Last Login', sortKey: 'last_login', render: (a) => formatLastLogin(a.last_login_at) },
  ], [])

  const activeColumns = useMemo(
    () => visibleColumns
      .map((key) => allColumns.find((col) => col.key === key))
      .filter((col): col is ColumnDef => col != null),
    [allColumns, visibleColumns]
  )

  const toggleColumn = useCallback((key: string) => {
    setVisibleColumns((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next))
      return next
    })
  }, [])

  // Column drag-to-reorder
  const [draggedCol, setDraggedCol] = useState<string | null>(null)
  const [dragOverCol, setDragOverCol] = useState<string | null>(null)

  const handleColDragStart = useCallback((key: string) => {
    setDraggedCol(key)
  }, [])

  const handleColDragOver = useCallback((e: React.DragEvent, key: string) => {
    e.preventDefault()
    setDragOverCol(key)
  }, [])

  const handleColDrop = useCallback((targetKey: string) => {
    if (!draggedCol || draggedCol === targetKey) {
      setDraggedCol(null)
      setDragOverCol(null)
      return
    }
    setVisibleColumns((prev) => {
      const next = [...prev]
      const fromIdx = next.indexOf(draggedCol)
      const toIdx = next.indexOf(targetKey)
      if (fromIdx === -1 || toIdx === -1) return prev
      next.splice(fromIdx, 1)
      next.splice(toIdx, 0, draggedCol)
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next))
      return next
    })
    setDraggedCol(null)
    setDragOverCol(null)
  }, [draggedCol])

  const handleColDragEnd = useCallback(() => {
    setDraggedCol(null)
    setDragOverCol(null)
  }, [])

  // --- CSV export ---
  const mapRowColumns = useCallback((agent: AgentListItem, cols: ColumnDef[]): Record<string, unknown> => {
    const row: Record<string, unknown> = {}
    for (const col of cols) {
      const val = col.render(agent)
      row[col.label] = typeof val === 'string' ? val : ''
    }
    return row
  }, [])

  const csvDataDisplayed = useMemo(() => agents.map((a) => mapRowColumns(a, activeColumns)), [agents, activeColumns, mapRowColumns])
  const csvDataAll = useMemo(() => agents.map((a) => mapRowColumns(a, allColumns)), [agents, allColumns, mapRowColumns])

  const fetchAllForExport = useCallback(async (cols: ColumnDef[]): Promise<Record<string, unknown>[]> => {
    if (!token || !pagination) return []
    const allData: Record<string, unknown>[] = []
    for (let p = 1; p <= pagination.total_pages; p++) {
      const res = await fetchAgentList(token, { ...apiParams, page: p })
      for (const agent of res.data) {
        allData.push(mapRowColumns(agent, cols))
      }
    }
    return allData
  }, [token, pagination, apiParams, mapRowColumns])

  // --- Styles ---
  const thStyle: React.CSSProperties = {
    textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`,
    color: table.headerText, fontWeight: 500, fontSize: '13px', cursor: 'pointer',
    userSelect: 'none', whiteSpace: 'nowrap',
  }
  const tdStyle: React.CSSProperties = { padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, fontSize: '14px' }
  const dateStr = format(new Date(), 'yyyy-MM-dd')

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Filter Panel */}
        <div style={{
          border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px',
          backgroundColor: '#FAFAFA', display: 'flex', flexDirection: 'column', gap: '12px',
        }}>
          {/* Row 1: Activity Period + Market */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ minWidth: '160px', flex: '0 1 200px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px', color: '#374151' }}>Activity Period</label>
              <select
                value={timeFrame}
                onChange={(e) => setTimeFrame(e.target.value as TimeFramePreset)}
                style={{
                  width: '100%', padding: '7px 10px', border: '1px solid #DEDEE2',
                  borderRadius: '6px', fontSize: '14px', fontFamily: 'SharpGrotesk, sans-serif',
                  backgroundColor: 'white', height: '36px', boxSizing: 'border-box',
                }}
              >
                {TIME_FRAME_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {timeFrame === 'custom' && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginTop: '8px' }}>
                  <div style={{ width: '140px' }}>
                    <DatePicker
                      label="From"
                      value={customStart}
                      onChange={setCustomStart}
                      maxDate={customEnd ?? undefined}
                      size="sm"
                    />
                  </div>
                  <Text variant="bodySm" color="muted" style={{ paddingBottom: '10px' }}>{'\u2013'}</Text>
                  <div style={{ width: '140px' }}>
                    <DatePicker
                      label="To"
                      value={customEnd}
                      onChange={setCustomEnd}
                      minDate={customStart ?? undefined}
                      maxDate={new Date()}
                      size="sm"
                    />
                  </div>
                </div>
              )}
            </div>
            <div style={{ minWidth: '200px', flex: '1 1 200px', maxWidth: '280px' }}>
              <Multiselect
                label="Markets"
                options={groupedMarketOptions}
                value={filterMarkets}
                onValueChange={setFilterMarkets}
                placeholder="All markets"
                size="sm"
                disabled={marketsLoading}
              />
            </div>
            <div style={{ minWidth: '160px', flex: '0 1 200px' }}>
              <label
                htmlFor="agent-type-filter"
                style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px', color: '#374151' }}
              >
                Agent Type
              </label>
              <select
                id="agent-type-filter"
                aria-label="Agent Type"
                value={agentTypeFilter}
                onChange={(e) => setAgentTypeFilter(e.target.value as AgentTypeFilter)}
                style={{
                  width: '100%', padding: '7px 10px', border: '1px solid #DEDEE2',
                  borderRadius: '6px', fontSize: '14px', fontFamily: 'SharpGrotesk, sans-serif',
                  backgroundColor: 'white', height: '36px', boxSizing: 'border-box',
                }}
              >
                {AGENT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          {/* Row 2: Last Login + Apply/Clear */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ minWidth: '160px', flex: '0 1 200px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px', color: '#374151' }}>Last Login</label>
              <select
                value={loginTimeFrame}
                onChange={(e) => setLoginTimeFrame(e.target.value as TimeFramePreset)}
                style={{
                  width: '100%', padding: '7px 10px', border: '1px solid #DEDEE2',
                  borderRadius: '6px', fontSize: '14px', fontFamily: 'SharpGrotesk, sans-serif',
                  backgroundColor: 'white', height: '36px', boxSizing: 'border-box',
                }}
              >
                {TIME_FRAME_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {loginTimeFrame === 'custom' && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginTop: '8px' }}>
                  <div style={{ width: '140px' }}>
                    <DatePicker
                      label="From"
                      value={loginCustomStart}
                      onChange={setLoginCustomStart}
                      maxDate={loginCustomEnd ?? undefined}
                      size="sm"
                    />
                  </div>
                  <Text variant="bodySm" color="muted" style={{ paddingBottom: '10px' }}>{'\u2013'}</Text>
                  <div style={{ width: '140px' }}>
                    <DatePicker
                      label="To"
                      value={loginCustomEnd}
                      onChange={setLoginCustomEnd}
                      minDate={loginCustomStart ?? undefined}
                      maxDate={new Date()}
                      size="sm"
                    />
                  </div>
                </div>
              )}
            </div>
            <Button variant="primary" size="sm" onClick={handleApplyFilters}>Apply Filters</Button>
            <Button variant="outline" size="sm" onClick={handleClearFilters}>Clear Filters</Button>
          </div>
        </div>

        {/* Tabs + Search + Column Picker + Export */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #E5E7EB' }}>
            {([
              { value: 'individual' as const, label: 'Individual Agents' },
              { value: 'by-market' as const, label: 'By Market' },
            ] as const).map((t) => (
              <button
                key={t.value}
                onClick={() => updateSearch({ tab: t.value })}
                style={{
                  padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer',
                  fontSize: '14px', fontWeight: 500, fontFamily: 'SharpGrotesk, sans-serif',
                  color: tab === t.value ? '#1E40AF' : '#6B7280',
                  borderBottom: tab === t.value ? '2px solid #1E40AF' : '2px solid transparent',
                  marginBottom: '-2px',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Search */}
            {tab === 'individual' && (
              <div style={{ width: '240px', flexShrink: 0 }}>
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search name, email..."
                  style={{
                    width: '100%', padding: '8px 12px', border: '1px solid #DEDEE2',
                    borderRadius: '6px', fontSize: '14px', fontFamily: 'SharpGrotesk, sans-serif',
                    outline: 'none', height: '36px', boxSizing: 'border-box',
                  }}
                />
              </div>
            )}
            {/* Column Picker */}
            {tab === 'individual' && (
              <div style={{ position: 'relative' }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowColumnPicker((v) => !v)}
                  aria-label="Configure columns"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM6.754 8a1.246 1.246 0 1 1 2.492 0 1.246 1.246 0 0 1-2.492 0z"/><path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.902 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319zm-2.633.283a.676.676 0 0 1 1.28 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16a.676.676 0 0 1 .906.906l-.16.292a1.873 1.873 0 0 0 1.116 2.692l.318.094a.676.676 0 0 1 0 1.28l-.318.094a1.873 1.873 0 0 0-1.116 2.693l.16.291a.676.676 0 0 1-.906.906l-.292-.16a1.873 1.873 0 0 0-2.692 1.116l-.094.318a.676.676 0 0 1-1.28 0l-.094-.318a1.873 1.873 0 0 0-2.693-1.116l-.291.16a.676.676 0 0 1-.906-.906l.16-.292a1.873 1.873 0 0 0-1.116-2.692l-.318-.094a.676.676 0 0 1 0-1.28l.318-.094a1.873 1.873 0 0 0 1.116-2.693l-.16-.291a.676.676 0 0 1 .906-.906l.292.16a1.873 1.873 0 0 0 2.692-1.116l.094-.318z"/></svg>
                  {' '}Columns
                </Button>
                {showColumnPicker && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 9 }} onClick={() => setShowColumnPicker(false)} />
                    <div style={{
                      position: 'absolute', top: '100%', right: 0, zIndex: 10, marginTop: '4px',
                      backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '8px 0', width: '220px',
                    }}>
                      {allColumns.filter((c) => c.optional).map((col) => (
                        <label
                          key={col.key}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px',
                            cursor: 'pointer', fontSize: '13px',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={visibleColumns.includes(col.key)}
                            onChange={() => toggleColumn(col.key)}
                          />
                          {col.label}
                        </label>
                      ))}
                      <div style={{ borderTop: '1px solid #E5E7EB', padding: '6px 12px' }}>
                        <Button variant="primary" size="sm" onClick={() => setShowColumnPicker(false)} style={{ width: '100%' }}>
                          Done
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            {tab === 'individual' ? (
              <ExportMenu options={[
                { label: 'Export Page \u2014 Displayed Columns', data: csvDataDisplayed, filename: `agents-${dateStr}.csv` },
                { label: 'Export Page \u2014 All Columns', data: csvDataAll, filename: `agents-all-cols-${dateStr}.csv` },
                ...(pagination && pagination.total_pages > 1 ? [
                  {
                    label: 'Export All \u2014 Displayed Columns',
                    description: `${pagination.total_count.toLocaleString()} agents`,
                    fetchData: () => fetchAllForExport(activeColumns),
                    filename: `agents-all-${dateStr}.csv`,
                  },
                  {
                    label: 'Export All \u2014 All Columns',
                    description: `${pagination.total_count.toLocaleString()} agents`,
                    fetchData: () => fetchAllForExport(allColumns),
                    filename: `agents-all-all-cols-${dateStr}.csv`,
                  },
                ] : []),
              ]} />
            ) : tab === 'by-market' ? (
              <ExportMenu options={[
                { label: 'Export CSV', data: mapAgentsByMarketPage(byMarketData), filename: agentsByMarketFilename() },
                ...(byMarketData.length > 0 ? [{
                  label: 'Export All',
                  fetchData: () => fetchAllAgentsByMarket(token!, byMarketFilters),
                  filename: agentsByMarketFilename(),
                }] : []),
              ]} />
            ) : null}
          </div>
        </div>

        {/* Individual Agents Tab */}
        {tab === 'individual' && (
          <div style={{ position: 'relative', minHeight: '400px' }}>
            <div className={styles.chartHeader}>
              <Text variant="bodySm" color="muted">
                {pagination ? `${pagination.total_count.toLocaleString()} agents` : 'Loading...'}
              </Text>
            </div>

            {agentsLoading && (
              <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>
                <LoadingSpinner size="sm" />
              </div>
            )}

            {agents.length > 0 ? (
              <>
                <InfoTooltip.Provider>
                  <div style={{ overflowX: 'auto' }}>
                    <table className={styles.recordsTable}>
                      <thead>
                        <tr>
                          {activeColumns.map((col) => (
                            <th
                              key={col.key}
                              draggable
                              onDragStart={() => handleColDragStart(col.key)}
                              onDragOver={(e) => handleColDragOver(e, col.key)}
                              onDrop={() => handleColDrop(col.key)}
                              onDragEnd={handleColDragEnd}
                              style={{
                                ...thStyle,
                                textAlign: col.align ?? 'left',
                                opacity: draggedCol === col.key ? 0.4 : 1,
                                borderLeft: dragOverCol === col.key && draggedCol !== col.key ? '2px solid #3B82F6' : undefined,
                              }}
                              onClick={col.sortKey ? () => handleSort(col.sortKey!) : undefined}
                            >
                              <ColumnHeader
                                label={col.label}
                                tooltip={COLUMN_TOOLTIPS[col.key] ?? col.label}
                                sortIndicator={col.sortKey ? sortIndicator(col.sortKey) : undefined}
                                align={col.align}
                              />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {agents.map((agent) => (
                          <tr key={agent.user_id} style={{ cursor: 'pointer' }} onClick={() => navigate({ to: '/admin/reporting/agents/$id', params: { id: agent.user_id } })}>
                            {activeColumns.map((col) => (
                              <td
                                key={col.key}
                                style={{
                                  ...tdStyle,
                                  textAlign: col.align ?? 'left',
                                  ...(col.key === 'name' ? { color: '#027F87', fontWeight: 500 } : {}),
                                }}
                              >
                                {col.render(agent)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </InfoTooltip.Provider>

                {pagination && (
                  <div className={styles.paginationControls}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Text variant="bodyXs" color="muted">
                        Page {pagination.page} of {pagination.total_pages}
                        {' '}({pagination.total_count.toLocaleString()} agents)
                      </Text>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Text variant="bodyXs" color="muted">Show</Text>
                        <select
                          value={pageSize}
                          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
                          style={{
                            padding: '2px 6px', border: '1px solid #DEDEE2', borderRadius: '4px',
                            fontSize: '12px', fontFamily: 'SharpGrotesk, sans-serif',
                            backgroundColor: 'white', cursor: 'pointer',
                          }}
                        >
                          {[10, 25, 50, 100, 200].map((n) => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                        <Text variant="bodyXs" color="muted">per page</Text>
                      </div>
                    </div>
                    <Stack direction="row" gap="8">
                      <Button variant="outline" size="sm" disabled={isFetching || pagination.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                        Previous
                      </Button>
                      <Button variant="outline" size="sm" disabled={isFetching || !pagination.has_more} onClick={() => setPage((p) => p + 1)}>
                        Next
                      </Button>
                    </Stack>
                  </div>
                )}
              </>
            ) : (
              !isFetching && (
                <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                  <Text variant="body" color="muted">No agents match the current filters</Text>
                </div>
              )
            )}
          </div>
        )}

        {/* By Market Tab */}
        {tab === 'by-market' && (
          <div style={{ position: 'relative', minHeight: '300px' }}>
            <div className={styles.chartHeader}>
              <Text variant="bodySm" color="muted">
                {byMarketData.length > 0 ? `${byMarketData.length} markets` : 'Loading...'}
              </Text>
            </div>

            {byMarketFetching && (
              <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>
                <LoadingSpinner size="sm" />
              </div>
            )}

            {byMarketData.length > 0 ? (
              <>
                <InfoTooltip.Provider>
                  <table className={styles.recordsTable}>
                    <thead>
                      <tr>
                        <th style={thStyle}>
                          <ColumnHeader label="Market" tooltip={BY_MARKET_TOOLTIPS.market} />
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>
                          <ColumnHeader label="Agents" tooltip={BY_MARKET_TOOLTIPS.agents} align="right" />
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>
                          <ColumnHeader label="Submitted" tooltip={BY_MARKET_TOOLTIPS.submitted} align="right" />
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>
                          <ColumnHeader label="w/ Offers" tooltip={BY_MARKET_TOOLTIPS.offers} align="right" />
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>
                          <ColumnHeader label="Sub to Offer" tooltip={BY_MARKET_TOOLTIPS.sub_to_offer} align="right" />
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>
                          <ColumnHeader label="Gone UC" tooltip={BY_MARKET_TOOLTIPS.uc} align="right" />
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>
                          <ColumnHeader label="Closed" tooltip={BY_MARKET_TOOLTIPS.closed} align="right" />
                        </th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>
                          <ColumnHeader label="UC to Close" tooltip={BY_MARKET_TOOLTIPS.uc_to_close} align="right" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {byMarketData.map((row: AgentsByMarketItem) => {
                        const isSelected = selectedMarket === row.market
                        const cellClick = (type: DrillDownType) => {
                          if (isSelected && drillDownType === type) {
                            setSelectedMarket(null)
                          } else {
                            setSelectedMarket(row.market)
                            setDrillDownType(type)
                            setDrillDownPage(1)
                          }
                        }
                        const clickableTd = (type: DrillDownType): React.CSSProperties => ({
                          ...tdStyle,
                          textAlign: 'right',
                          cursor: 'pointer',
                          backgroundColor: isSelected && drillDownType === type ? '#DBEAFE' : isSelected ? '#F0F9FF' : undefined,
                        })
                        return (
                          <tr key={row.market} style={{ backgroundColor: isSelected ? '#F0F9FF' : undefined }}>
                            <td style={tdStyle}>{row.market}</td>
                            <td style={clickableTd('agents')} onClick={() => cellClick('agents')}>{row.agent_count.toLocaleString()}</td>
                            <td style={clickableTd('submissions')} onClick={() => cellClick('submissions')}>{row.properties_submitted.toLocaleString()}</td>
                            <td style={clickableTd('offers')} onClick={() => cellClick('offers')}>{row.properties_with_offers.toLocaleString()}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatRate(row.submission_to_offer_rate)}</td>
                            <td style={clickableTd('uc')} onClick={() => cellClick('uc')}>{row.gone_uc.toLocaleString()}</td>
                            <td style={clickableTd('closed')} onClick={() => cellClick('closed')}>{row.closed.toLocaleString()}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatRate(row.uc_to_close_rate)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                    {(() => {
                      const totals = byMarketData.reduce((acc, row) => ({
                        agent_count: acc.agent_count + row.agent_count,
                        properties_submitted: acc.properties_submitted + row.properties_submitted,
                        properties_with_offers: acc.properties_with_offers + row.properties_with_offers,
                        gone_uc: acc.gone_uc + row.gone_uc,
                        closed: acc.closed + row.closed,
                      }), { agent_count: 0, properties_submitted: 0, properties_with_offers: 0, gone_uc: 0, closed: 0 })
                      const totalSubRate = totals.properties_submitted > 0
                        ? (totals.properties_with_offers / totals.properties_submitted) * 100
                        : null
                      const totalCloseRate = (totals.gone_uc + totals.closed) > 0
                        ? (totals.closed / (totals.gone_uc + totals.closed)) * 100
                        : null
                      const tfStyle: React.CSSProperties = { ...tdStyle, fontWeight: 600, borderTop: '2px solid #D1D5DB' }
                      return (
                        <tfoot>
                          <tr>
                            <td style={tfStyle}>Total</td>
                            <td style={{ ...tfStyle, textAlign: 'right' }}>{totals.agent_count.toLocaleString()}</td>
                            <td style={{ ...tfStyle, textAlign: 'right' }}>{totals.properties_submitted.toLocaleString()}</td>
                            <td style={{ ...tfStyle, textAlign: 'right' }}>{totals.properties_with_offers.toLocaleString()}</td>
                            <td style={{ ...tfStyle, textAlign: 'right' }}>{totalSubRate !== null ? `${totalSubRate.toFixed(1)}%` : 'N/A'}</td>
                            <td style={{ ...tfStyle, textAlign: 'right' }}>{totals.gone_uc.toLocaleString()}</td>
                            <td style={{ ...tfStyle, textAlign: 'right' }}>{totals.closed.toLocaleString()}</td>
                            <td style={{ ...tfStyle, textAlign: 'right' }}>{totalCloseRate !== null ? `${totalCloseRate.toFixed(1)}%` : 'N/A'}</td>
                          </tr>
                        </tfoot>
                      )
                    })()}
                  </table>
                </InfoTooltip.Provider>

                {/* Drill-Down Panel */}
                {selectedMarket && (
                  <div className={styles.drillDownPanel} style={{ marginTop: '16px' }}>
                    <div className={styles.drillDownHeader}>
                      <Text variant="body" style={{ fontWeight: 600 }}>
                        {drillDownType === 'agents' ? 'Agents' : drillDownType === 'submissions' ? 'Submissions' : drillDownType === 'offers' ? 'Offers' : drillDownType === 'uc' ? 'Under Contract' : 'Closed'} in {selectedMarket}
                        {drillDownPagination && (
                          <Text as="span" variant="bodySm" color="muted" style={{ fontWeight: 400, marginLeft: '8px' }}>
                            ({drillDownPagination.total_count.toLocaleString()})
                          </Text>
                        )}
                      </Text>
                      <button
                        onClick={() => setSelectedMarket(null)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', color: '#6B7280' }}
                        aria-label="Close drill-down"
                      >
                        <Icon name="close" size={18} />
                      </button>
                    </div>

                    <div style={{ position: 'relative', minHeight: '200px' }}>
                      {drillDownFetching && (
                        <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>
                          <LoadingSpinner size="sm" />
                        </div>
                      )}

                      {/* Agent drill-down (populates individual table) */}
                      {isAgentDrill && (() => {
                        const data = agentDrillRes?.data ?? []
                        return data.length > 0 ? (
                          <>
                            <table className={styles.recordsTable}>
                              <thead>
                                <tr>
                                  <th style={thStyle}>Agent Name</th>
                                  <th style={thStyle}>Email</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Submitted</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>w/ Offers</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Sub to Offer</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Gone UC</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Closed</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>UC to Close</th>
                                  <th style={thStyle}>Last Login</th>
                                </tr>
                              </thead>
                              <tbody>
                                {data.map((agent: AgentListItem) => (
                                  <tr key={agent.user_id} style={{ cursor: 'pointer' }} onClick={() => navigate({ to: '/admin/reporting/agents/$id', params: { id: agent.user_id } })}>
                                    <td style={{ ...tdStyle, color: '#027F87', fontWeight: 500 }}>{agent.first_name} {agent.last_name}</td>
                                    <td style={tdStyle}>{agent.email ?? '-'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>{agent.properties_submitted.toLocaleString()}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>{agent.properties_with_offers.toLocaleString()}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatRate(agent.submission_to_offer_rate)}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>{agent.gone_uc.toLocaleString()}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>{agent.closed.toLocaleString()}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatRate(agent.uc_to_close_rate)}</td>
                                    <td style={tdStyle}>{formatLastLogin(agent.last_login_at)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {drillDownPagination && drillDownPagination.total_pages > 1 && (
                              <div className={styles.paginationControls}>
                                <Text variant="bodyXs" color="muted">
                                  Page {drillDownPagination.page} of {drillDownPagination.total_pages}
                                </Text>
                                <Stack direction="row" gap="8">
                                  <Button variant="outline" size="sm" disabled={drillDownFetching || drillDownPagination.page <= 1} onClick={() => setDrillDownPage((p) => Math.max(1, p - 1))}>Previous</Button>
                                  <Button variant="outline" size="sm" disabled={drillDownFetching || !drillDownPagination.has_more} onClick={() => setDrillDownPage((p) => p + 1)}>Next</Button>
                                </Stack>
                              </div>
                            )}
                          </>
                        ) : (
                          !drillDownFetching && (
                            <div style={{ textAlign: 'center', padding: '24px' }}>
                              <Text variant="body" color="muted">No agents found</Text>
                            </div>
                          )
                        )
                      })()}

                      {/* Activity drill-down (submissions/offers/uc/closed) */}
                      {!isAgentDrill && (() => {
                        const data = activityDrillRes?.data ?? []
                        if (data.length === 0 && !drillDownFetching) {
                          return (
                            <div style={{ textAlign: 'center', padding: '24px' }}>
                              <Text variant="body" color="muted">No records found</Text>
                            </div>
                          )
                        }
                        if (data.length === 0) return null

                        // Submissions drill-down
                        if (drillDownType === 'submissions') {
                          return (
                            <>
                              <table className={styles.recordsTable}>
                                <thead>
                                  <tr>
                                    <th style={thStyle}>Agent Name</th>
                                    <th style={thStyle}>Property Address</th>
                                    <th style={thStyle}>Market</th>
                                    <th style={thStyle}>Date</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {data.map((rec: MarketActivityRecordItem) => (
                                    <tr key={rec.id}>
                                      <td style={tdStyle}>{rec.agent_name ?? '-'}</td>
                                      <td style={tdStyle}>{rec.property_address ?? '-'}</td>
                                      <td style={tdStyle}>{rec.market ?? '-'}</td>
                                      <td style={tdStyle}>{rec.date ? formatDate(rec.date) : '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {drillDownPagination && drillDownPagination.total_pages > 1 && (
                                <div className={styles.paginationControls}>
                                  <Text variant="bodyXs" color="muted">
                                    Page {drillDownPagination.page} of {drillDownPagination.total_pages}
                                  </Text>
                                  <Stack direction="row" gap="8">
                                    <Button variant="outline" size="sm" disabled={drillDownFetching || drillDownPagination.page <= 1} onClick={() => setDrillDownPage((p) => Math.max(1, p - 1))}>Previous</Button>
                                    <Button variant="outline" size="sm" disabled={drillDownFetching || !drillDownPagination.has_more} onClick={() => setDrillDownPage((p) => p + 1)}>Next</Button>
                                  </Stack>
                                </div>
                              )}
                            </>
                          )
                        }

                        // Offers drill-down
                        if (drillDownType === 'offers') {
                          return (
                            <>
                              <table className={styles.recordsTable}>
                                <thead>
                                  <tr>
                                    <th style={thStyle}>Agent Name</th>
                                    <th style={thStyle}>Investor</th>
                                    <th style={thStyle}>Property Address</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Offer Price</th>
                                    <th style={thStyle}>Date</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {data.map((rec: MarketActivityRecordItem) => (
                                    <tr key={rec.id}>
                                      <td style={tdStyle}>{rec.agent_name ?? '-'}</td>
                                      <td style={tdStyle}>{rec.investor_name ?? '-'}</td>
                                      <td style={tdStyle}>{rec.property_address ?? '-'}</td>
                                      <td style={{ ...tdStyle, textAlign: 'right' }}>{rec.value != null ? `$${rec.value.toLocaleString()}` : '-'}</td>
                                      <td style={tdStyle}>{rec.date ? formatDate(rec.date) : '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {drillDownPagination && drillDownPagination.total_pages > 1 && (
                                <div className={styles.paginationControls}>
                                  <Text variant="bodyXs" color="muted">
                                    Page {drillDownPagination.page} of {drillDownPagination.total_pages}
                                  </Text>
                                  <Stack direction="row" gap="8">
                                    <Button variant="outline" size="sm" disabled={drillDownFetching || drillDownPagination.page <= 1} onClick={() => setDrillDownPage((p) => Math.max(1, p - 1))}>Previous</Button>
                                    <Button variant="outline" size="sm" disabled={drillDownFetching || !drillDownPagination.has_more} onClick={() => setDrillDownPage((p) => p + 1)}>Next</Button>
                                  </Stack>
                                </div>
                              )}
                            </>
                          )
                        }

                        // UC / Closed drill-down
                        return (
                          <>
                            <table className={styles.recordsTable}>
                              <thead>
                                <tr>
                                  <th style={thStyle}>Agent Name</th>
                                  <th style={thStyle}>Property Address</th>
                                  <th style={thStyle}>Market</th>
                                  <th style={thStyle}>{drillDownType === 'uc' ? 'UC Date' : 'Closing Date'}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {data.map((rec: MarketActivityRecordItem) => (
                                  <tr key={rec.id}>
                                    <td style={tdStyle}>{rec.agent_name ?? '-'}</td>
                                    <td style={tdStyle}>{rec.property_address ?? '-'}</td>
                                    <td style={tdStyle}>{rec.market ?? '-'}</td>
                                    <td style={tdStyle}>{rec.date ? formatDate(rec.date) : '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {drillDownPagination && drillDownPagination.total_pages > 1 && (
                              <div className={styles.paginationControls}>
                                <Text variant="bodyXs" color="muted">
                                  Page {drillDownPagination.page} of {drillDownPagination.total_pages}
                                </Text>
                                <Stack direction="row" gap="8">
                                  <Button variant="outline" size="sm" disabled={drillDownFetching || drillDownPagination.page <= 1} onClick={() => setDrillDownPage((p) => Math.max(1, p - 1))}>Previous</Button>
                                  <Button variant="outline" size="sm" disabled={drillDownFetching || !drillDownPagination.has_more} onClick={() => setDrillDownPage((p) => p + 1)}>Next</Button>
                                </Stack>
                              </div>
                            )}
                          </>
                        )
                      })()}
                    </div>
                  </div>
                )}
              </>
            ) : (
              !byMarketFetching && (
                <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                  <Text variant="body" color="muted">No market data available for the current filters</Text>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  )
}
